/* eslint-disable no-param-reassign */
/* eslint import/no-webpack-loader-syntax: off */
import {
  DateHelper,
  DomHelper,
  DomSync,
  DragHelper,
  EventHelper,
  SchedulerProConfig,
} from '@bryntum/schedulerpro';
import { BryntumSchedulerPro } from '@bryntum/schedulerpro-react';
import { merge } from 'lodash';
import moment, { unitOfTime } from 'moment';
import React, {
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {
  AllocationMedium,
  PlanningLevelConfig,
  ResourceManagementConfig,
} from 'api/resource-management/types';
import { humanise } from 'components/atoms/humanised-date';
import { ColumnConfig } from 'components/organisms/resource-management/scheduler-config/columns/types';
import { ResizeEnd } from 'components/organisms/resource-management/scheduler-config/listeners';
import {
  AllocationEventStyles,
  AvailabilityRangeStyles,
  CustomMaskStyles,
  DemandEventStyles,
  DemandGridStyles,
  DemandRoleSummaryEventStyles,
  EventStyles,
  ExpanderIconStyles,
  GridStyles,
  LoaderStyles,
  NoResultStyles,
  ProjectEventStyles,
  ResourceSpecificGridStyles,
  ShowActionOnHover,
  SplitModeStyles,
  TaskIntensityStyles,
  TeamSummaryEventStyles,
  TimeoffEventStyles,
  UserSummaryEventStyles,
  ViewPresetHeaderStyles,
} from 'components/organisms/resource-management/scheduler-config/styles';
import { demandCapacityTemplate } from 'components/organisms/resource-management/scheduler-config/templates/demand-capacity-template';
import { demandEventTemplate } from 'components/organisms/resource-management/scheduler-config/templates/demand-event-template';
import { demandRoleTemplate } from 'components/organisms/resource-management/scheduler-config/templates/demand-role-template';
import { totalAllocationTemplate } from 'components/organisms/resource-management/scheduler-config/templates/demand-total-allocation-template';
import { EventMoreDropdown } from 'components/organisms/resource-management/scheduler-config/templates/event-more-actions';
import { projectPhaseTemplate } from 'components/organisms/resource-management/scheduler-config/templates/project-phase-template';
import { projectTimelineTemplate } from 'components/organisms/resource-management/scheduler-config/templates/project-timeline-template';
import { teamSummaryTemplate } from 'components/organisms/resource-management/scheduler-config/templates/team-summary-template';
import { timeOffTemplate } from 'components/organisms/resource-management/scheduler-config/templates/timeoff-template';
import { userSummaryTemplate } from 'components/organisms/resource-management/scheduler-config/templates/user-summary-template';
import {
  getMinimumDaysForDragAffordanceVisibility,
  getMinimumDaysForMoreActionsVisibility,
  getMinimumNumberOfDaysForTextToBeVisible,
  ViewPreset,
} from 'components/organisms/resource-management/scheduler-config/view-presets/view-presets';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { SchedulerEventType } from '../event-types';
import { SchedulerResourceType } from '../resource-types';
import { companySummaryTemplate } from '../scheduler-config/templates/company-summary-template';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import {
  ignoreMinutesIfZero,
  shiftDateIfOnNonWorkingDay,
} from '../utils/dates';
import { isOverlapping, PHASE_STYLE } from '../utils/events-conversion/common';
import SchedulerCss from '!!raw-loader!@bryntum/schedulerpro/thin/scheduler.classic-light.thin.module.css';

const SchedulerPro = styled.div`
  width: 100%;
  height: 100%;
  ${SchedulerCss};
  ${ViewPresetHeaderStyles};
  ${ExpanderIconStyles};
  ${GridStyles};
  ${ResourceSpecificGridStyles};
  ${ShowActionOnHover};
  ${ProjectEventStyles};
  ${TaskIntensityStyles};
  ${SplitModeStyles};
  ${AllocationEventStyles};
  ${UserSummaryEventStyles};
  ${TeamSummaryEventStyles};
  ${EventStyles};
  ${AvailabilityRangeStyles};
  ${NoResultStyles};
  ${CustomMaskStyles};
  ${DemandRoleSummaryEventStyles};
  ${DemandEventStyles};
  ${DemandGridStyles};
  ${LoaderStyles};
  ${TimeoffEventStyles};

  .placeholder {
    height: 32px;
    position: absolute;
    border-radius: 4px;
    z-index: 1;
    padding-right: 2px;
    cursor: pointer;

    div {
      display: flex;
      color: var(--white-100);
      align-items: center;
      justify-content: center;
      pointer-events: none;
      height: 100%;
      border-radius: 4px;
      background-color: var(--blue-60);
    }

    &:before,
    &:after {
      content: '';
      width: 4px;
      height: 50%;
      top: 25%;
      position: absolute;
      border-style: solid;
      border-color: rgba(255, 255, 255, 0.5);
      pointer-events: none;
      z-index: 7;
    }

    &:before {
      margin-left: 3px;
      left: 0;
      border-width: 0 1px;
    }

    &:after {
      margin-right: 3px;
      right: 2px;
      border-width: 0 1px;
    }

    &.b-drag-original {
      z-index: 3;
      background-color: var(--blue-60);
      cursor: ew-resize;
    }
  }
`;

interface Props {
  columns: ColumnConfig[];
  newEventAllowedOn: SchedulerResourceType[];
  zoomPresets: ViewPreset[];
  workingDaysConfig: WorkingDaysConfiguration;
  schedulerConfig?: Partial<SchedulerProConfig>;
  className?: string;
  currentViewPreset?: ViewPresetId;
  resourceManagementConfig?: ResourceManagementConfig;
}

export const Scheduler = forwardRef<any, Props>(
  (
    {
      columns,
      newEventAllowedOn,
      zoomPresets,
      workingDaysConfig,
      schedulerConfig: externalSchedulerConfig,
      className,
      resourceManagementConfig,
    },
    externalRef,
  ) => {
    const internalRef = useRef<any>();
    const translate = useTranslate();
    const ref = (externalRef as MutableRefObject<any>) || internalRef;

    const dateToPx = useCallback(
      (date, left) =>
        (ref.current as unknown as any)?.instance.getCoordinateFromDate(date) -
        left,
      [ref],
    );
    const isHoursPerWeek = useMemo(
      () =>
        resourceManagementConfig?.planningLevelConfig ===
        PlanningLevelConfig.HoursPerWeek,
      [resourceManagementConfig?.planningLevelConfig],
    );
    const isDraggingRef = useRef<boolean>();
    const placeholderProperties = useRef<any>();

    const placeholder = useMemo(
      () =>
        DomHelper.createElement({
          tag: 'div',
          className: 'placeholder',
          style: 'height: 32px;display: none;',
          children: [{ html: '<span>+</span>' }],
        }),
      [],
    );

    const addNewAllocation = useCallback(
      (startDate, endDate, resourceId) => {
        const resource = ref.current.instance.resourceStore.getById(resourceId);

        ref.current.instance.trigger('onCustomEventAdd', {
          startDate,
          endDate,
          resource,
          allocationMedium: AllocationMedium.Hard,
        });
      },
      [ref],
    );
    const deleteEvent = useCallback(
      (event) => {
        ref.current.instance.trigger('onCustomEventDelete', { event });
      },
      [ref],
    );

    useEffect(() => {
      EventHelper.addListener(placeholder as HTMLElement, 'click', (event) => {
        const { left, width } = DomHelper.getStyleValue(
          placeholder as HTMLElement,
          ['left', 'width', 'top'],
        ) as { left: string; width: string; top: string };
        const startDate = ref.current.instance.getDateFromCoordinate(
          parseInt(left, 10),
        );
        const endDate = ref.current.instance.getDateFromCoordinate(
          parseInt(left, 10) + parseInt(width, 10),
        );
        const [, y] = EventHelper.getXY(event);
        const rowElement = document.elementFromPoint(50, y);
        const resource = ref.current.instance.resolveResourceRecord(rowElement);

        DomHelper.applyStyle(placeholder as HTMLElement, {
          display: 'none',
        });
        if (resource) {
          addNewAllocation(
            moment(startDate).startOf('day').toDate(),
            moment(endDate).endOf('day').toDate(),
            resource.id,
          );
        }
        ref.current.instance.suspendRefresh();
        ref.current.instance.eventStore.allRecords
          .filter((x) => x.splitModeActive)
          .forEach((x) => x.set('splitModeActive', false));
        ref.current.instance.resumeRefresh(true);
      });
    }, [addNewAllocation, placeholder, ref]);

    useEffect(() => {
      const dragHelper = new DragHelper({
        mode: 'translateXY',
        targetSelector: '.placeholder',
        cloneTarget: true,
        lockY: true,
        dragThreshold: 1,
        createProxy: (element) => element.cloneNode(),
        listeners: {
          abort: () => {
            isDraggingRef.current = false;
            ref.current.instance.disableScrollingCloseToEdges(
              ref.current.instance.timeAxisSubGrid,
            );
          },
          reset: () => {
            isDraggingRef.current = false;
            ref.current.instance.disableScrollingCloseToEdges(
              ref.current.instance.timeAxisSubGrid,
            );
          },
          beforeDragStart: () => {
            isDraggingRef.current = true;
            ref.current.instance.enableScrollingCloseToEdges(
              ref.current.instance.timeAxisSubGrid,
            );
          },
          drag: (event) => {
            if (!placeholderProperties.current) {
              return;
            }
            const { newX } = event.context;
            const { width, top, dragStartDate } = placeholderProperties.current;

            const dateOnCurrentCursorPosition = moment(
              ref.current.instance.getDateFromCoordinate(
                newX + width / 2,
                '',
                false,
              ),
            );
            const dragStartedDate = moment(dragStartDate);
            const zoomLevel = ref.current?.instance.zoomLevel;
            const { incrementBy } = zoomPresets[zoomLevel];
            const visibleStartDate = moment(
              ref.current.instance.visibleDateRange.startDate,
            );
            const visibleEndDate = moment(
              ref.current.instance.visibleDateRange.endDate,
            );

            let barStartDate;
            let barEndDate;

            if (
              dateOnCurrentCursorPosition.isSameOrAfter(dragStartedDate, 'day')
            ) {
              barStartDate = isHoursPerWeek
                ? dragStartedDate.startOf('week')
                : dragStartedDate.startOf(
                    (incrementBy as unitOfTime.StartOf) ?? 'day',
                  );
              barEndDate = isHoursPerWeek
                ? dateOnCurrentCursorPosition.endOf('week')
                : dateOnCurrentCursorPosition.endOf(
                    (incrementBy as unitOfTime.StartOf) ?? 'day',
                  );
              placeholderProperties.current = {
                ...placeholderProperties.current,
                draggingEndDate: barEndDate.toDate(),
              };
            } else {
              barStartDate = isHoursPerWeek
                ? dateOnCurrentCursorPosition.startOf('week')
                : dateOnCurrentCursorPosition.startOf(
                    (incrementBy as unitOfTime.StartOf) ?? 'day',
                  );
              barEndDate = isHoursPerWeek
                ? dragStartedDate.endOf('week')
                : dragStartedDate.endOf(
                    (incrementBy as unitOfTime.StartOf) ?? 'day',
                  );
              placeholderProperties.current = {
                ...placeholderProperties.current,
                draggingEndDate: barStartDate.toDate(),
              };
            }

            const newLeft =
              ref.current.instance.getCoordinateFromDate(
                barStartDate.isBefore(visibleStartDate, 'day')
                  ? visibleStartDate.toDate()
                  : barStartDate.toDate(),
                {
                  local: true,
                },
              ) + 1;
            const newWidth =
              ref.current.instance.getTimeSpanDistance(
                barStartDate.isBefore(visibleStartDate, 'day')
                  ? visibleStartDate.toDate()
                  : barStartDate.toDate(),
                barEndDate.isAfter(visibleEndDate, 'day')
                  ? visibleEndDate.toDate()
                  : barEndDate.toDate(),
              ) - 2;

            DomSync.sync({
              domConfig: {
                children: [{ html: '<span>+</span>' }],
                dataset: {
                  placeholder: '',
                },
                style: {
                  display: 'block',
                  left: `${newLeft}px`,
                  width: `${newWidth}px`,
                  top: `${top}px`,
                },
              },
              targetElement: placeholder,
            });
          },
          drop: (event) => {
            const { dragStartDate, draggingEndDate } =
              placeholderProperties.current;
            const startDateMoment = moment(dragStartDate);
            const endDateMoment = moment(draggingEndDate);
            const { resourceId } = event.source.context.element.dataset;

            event.context.finalize?.(true);
            DomHelper.applyStyle(placeholder as HTMLElement, {
              display: 'none',
            });
            if (resourceId) {
              addNewAllocation(
                (startDateMoment.isSameOrBefore(endDateMoment)
                  ? startDateMoment
                  : endDateMoment
                )
                  .startOf('day')
                  .toDate(),
                (endDateMoment.isSameOrAfter(startDateMoment)
                  ? endDateMoment
                  : startDateMoment
                )
                  .endOf('day')
                  .toDate(),
                resourceId,
              );
            }
            isDraggingRef.current = false;
            ref.current.instance.disableScrollingCloseToEdges(
              ref.current.instance.timeAxisSubGrid,
            );
          },
        },
      });

      return () => dragHelper.destroy();
    }, [addNewAllocation, isHoursPerWeek, placeholder, ref, zoomPresets]);

    const schedulerConfig = useMemo(
      () => ({
        infiniteScroll: true,
        rowHeight: 64,
        barMargin: 0,
        eventLayout: { type: 'none' },
        bufferCoef: 1.5,
        bufferThreshold: 0.2,
        viewPreset: ViewPresetId.Week,
        presets: zoomPresets,
        zoomOnMouseWheel: false,
        zoomOnTimeAxisDoubleClick: false,
        managedEventSizing: false,
        createEventOnDblClick: false,
        zoomKeepsOriginalTimespan: false,
        preserveScrollOnDatasetChange: true,
        listeners: {
          beforeToggleNode: (event) => {
            if (
              event.record.type === SchedulerResourceType.Project &&
              event.collapse === false
            ) {
              (
                event.record.unfilteredChildren ?? event.record.children
              ).forEach(({ id }) => {
                event.source.expand(id);
              });
            }

            return false;
          },
          toggleNode: (params) => {
            if (params.record.type !== SchedulerResourceType.Project) {
              return;
            }
            const resource = ref.current.instance.resourceStore.getById(
              params.record.id,
            );

            ref.current?.instance.trigger('onProjectRowHover', {
              projectId: resource.get('projectId'),
            });

            resource.getEvents().forEach((event) => {
              event.set('showInDetail', !params.collapse);
            });
            const row = ref.current.instance.getRowFor(resource);

            if (row) {
              row.elements.locked.classList.add('hover');
              row.elements.normal.classList.add('hover');
            }
          },

          beforeEventResizeFinalize: (params) => {
            const {
              context: { toSet, snappedDate, resizedRecord, oldValue },
            } = params;

            params.context.async = true;
            params.context.finalize(false);

            if (
              toSet === ResizeEnd.EndDate &&
              moment(snappedDate)
                .subtract(1, 'day')
                .endOf('day')
                .isSame(moment(oldValue), 'day')
            ) {
              return;
            }

            ref.current.instance.trigger('onCustomEventResize', {
              event: resizedRecord.event,
              resizedAt: toSet,
              ...(toSet === 'startDate'
                ? {
                    newStartDate: moment(snappedDate)
                      .add(1, 'second')
                      .startOf('day')
                      .toDate(),
                    oldStartDate: oldValue,
                  }
                : {
                    newEndDate: moment(snappedDate)
                      .subtract(1, 'day')
                      .endOf('day')
                      .toDate(),
                    oldEndDate: oldValue,
                  }),
            });
          },
          beforeeventdropfinalize: (params) => {
            const {
              context: { origEnd, origStart, startDate, endDate, eventRecord },
            } = params;

            isDraggingRef.current = false;
            params.context.async = true;
            params.context.finalize(false);

            ref.current.instance.trigger('onCustomEventDrag', {
              event: eventRecord,
              from: {
                startDate: origStart,
                endDate: origEnd,
              },
              to: {
                startDate,
                endDate,
              },
            });
          },
          eventMouseOver: (arg) => {
            const { eventRecord, source } = arg;

            if (isDraggingRef.current) {
              return;
            }

            DomHelper.applyStyle(placeholder as HTMLElement, {
              display: 'none',
            });

            if (eventRecord == null) {
              return;
            }

            if (
              eventRecord.type === SchedulerEventType.ProjectPhase &&
              !eventRecord.activePhase
            ) {
              ref.current.instance.suspendRefresh();
              ref.current.instance.eventStore.beginBatch();
              const currentGroupPhaseEvents = eventRecord.resource
                .getEvents()
                .filter(
                  ({ type, groupKey }) =>
                    type === SchedulerEventType.ProjectPhase &&
                    groupKey === eventRecord.groupKey,
                );

              currentGroupPhaseEvents.forEach((event) => {
                event.activePhase = eventRecord.rowIndex === event.rowIndex;
                const isBelowActive = eventRecord.rowIndex < event.rowIndex;
                const top =
                  event.rowIndex === 0
                    ? PHASE_STYLE.defaultTop
                    : PHASE_STYLE.defaultTop +
                      (!isBelowActive ? 0 : PHASE_STYLE.defaultHeight) +
                      event.height *
                        (event.rowIndex - (!isBelowActive ? 0 : 1)) +
                      event.rowIndex;

                event.top = top;
              });
              ref.current.instance.eventStore.endBatch(true);
              ref.current.instance.resumeRefresh(true);
            }

            const moreActionContainer = document.querySelector(
              `[data-event-id="${eventRecord.id}"] .b-sch-event-content .more-actions-container`,
            );

            if (moreActionContainer) {
              const { startDate } = eventRecord;
              const { endDate } = eventRecord;
              const zoomLevel = ref.current?.instance.zoomLevel;
              const { incrementBy } = zoomPresets[zoomLevel];
              const hideSplit = moment(startDate).isSame(
                moment(endDate),
                (incrementBy as unitOfTime.StartOf) ?? 'day',
              );

              ReactDOM.render(
                <EventMoreDropdown
                  translate={translate}
                  hideSplit={hideSplit}
                  onEdit={() => {
                    const event = source.eventStore.getById(eventRecord.id);

                    if (event != null) {
                      ref.current.instance.trigger('onCustomEventEdit', {
                        event,
                      });
                    }
                  }}
                  onSplit={() => {
                    source.eventStore.getById(eventRecord.id).splitModeActive =
                      true;
                  }}
                  onRemove={() => {
                    /**
                     * Deleting the event after few microseconds to fix the
                     * issue of dropdown being rendered wrt to body otherwise
                     */
                    window.setTimeout(() => {
                      deleteEvent(eventRecord);
                    }, 200);
                  }}
                />,
                moreActionContainer,
              );
            }
          },

          eventClick: (arg) => {
            const { eventRecord } = arg;
            const eventType = eventRecord.get('type');
            const { target } = arg.event;
            const splitAt =
              target.getAttribute('data-split-at') ??
              target.closest('.splitter')?.getAttribute('data-split-at');

            if (splitAt) {
              ref.current.instance.trigger('onCustomEventSplit', {
                event: eventRecord,
                splitAt: moment(splitAt).startOf('day').toDate(),
              });

              return;
            }
            if (!eventRecord.splitModeActive) {
              ref.current.instance.suspendRefresh();
              ref.current.instance.eventStore.allRecords
                .filter((event) => event.splitModeActive)
                .forEach((event) => event.set('splitModeActive', false));
              ref.current.instance.resumeRefresh(true);
            }
            const moreActionsContainer = target.closest(
              '.more-actions-container',
            );

            switch (eventType) {
              case SchedulerEventType.Allocation:
                if (
                  moreActionsContainer &&
                  arg.event.path.indexOf(moreActionsContainer) !== -1
                ) {
                  return;
                }

                if (eventRecord.readOnly === true) {
                  ref.current.instance.trigger('onShowSourceTasks', {
                    event: eventRecord,
                  });
                } else {
                  ref.current.instance.trigger('onCustomEventEdit', {
                    event: eventRecord,
                  });
                }
                break;
              case SchedulerEventType.UserAllocationSummary:
              case SchedulerEventType.TeamAllocationSummary:
              case SchedulerEventType.CompanyAllocationSummary:
              case SchedulerEventType.DemandRole:
              case SchedulerEventType.TotalAllocation:
              case SchedulerEventType.ProjectPhase:
              case SchedulerEventType.ProjectTimeline:
                ref.current.instance.expand(eventRecord.get('resourceId'));
                break;
              default:
                break;
            }
          },

          mouseOut: (params) => {
            try {
              const { event } = params;
              let resource = ref.current.instance.resolveResourceRecord(
                event.target,
              );

              if (resource == null) {
                const [, y] = EventHelper.getXY(event);
                const rowElement = document.elementFromPoint(50, y);

                resource =
                  ref.current.instance.resolveResourceRecord(rowElement);
              }
              const row = ref.current.instance.getRowFor(resource);

              if (row) {
                if (
                  resource &&
                  resource.type === SchedulerResourceType.Project &&
                  row.cls['b-tree-collapsed']
                ) {
                  resource.getEvents().forEach((e) => {
                    e.set('showInDetail', false);
                  });
                }
                row.elements.locked.classList.remove('hover');
                row.elements.normal.classList.remove('hover');
              }
            } catch (error) {
              logger.info('error in scheduler mouse out', { error });
            }
          },
          mouseOver: (params) => {
            try {
              const { event } = params;
              let resource = ref.current.instance.resolveResourceRecord(
                event.target,
              );

              if (resource == null) {
                const [, y] = EventHelper.getXY(event);
                const rowElement = document.elementFromPoint(50, y);

                resource =
                  ref.current.instance.resolveResourceRecord(rowElement);
              }
              const row = ref.current.instance.getRowFor(resource);

              if (row) {
                const alreadyHoveredRowElement =
                  ref.current.instance.timeAxisSubGridElement.querySelector(
                    '.b-grid-row.hover',
                  );

                if (alreadyHoveredRowElement) {
                  const alreadyHoveredRow =
                    ref.current.instance.getRowFromElement(
                      alreadyHoveredRowElement,
                    );

                  if (alreadyHoveredRow) {
                    alreadyHoveredRow.elements.locked.classList.remove('hover');
                    alreadyHoveredRow.elements.normal.classList.remove('hover');
                  }
                }
                if (
                  resource &&
                  resource.type === SchedulerResourceType.Project
                ) {
                  ref.current?.instance.trigger('onProjectRowHover', {
                    projectId: resource.get('projectId'),
                  });
                  /**
                   * Commenting below code to not show phases on hover; design ask;
                   * Any more changes needed for this ?
                   */
                  // resource.getEvents().forEach((e) => {
                  //   e.set('showInDetail', true);
                  // });
                }
                row.elements.locked.classList.add('hover');
                row.elements.normal.classList.add('hover');
              }
            } catch (error) {
              logger.info('error in scheduler mouse move', { error });
            }
          },
          cellMouseOut: (event) => {
            if (
              event.event?.toElement?.classList.contains('placeholder') ===
                false &&
              isDraggingRef.current === false
            ) {
              DomHelper.applyStyle(placeholder as HTMLElement, {
                display: 'none',
              });
            }
          },
          cellClick: () => {
            ref.current.instance.suspendRefresh();
            ref.current.instance.eventStore.allRecords
              .filter((event) => event.splitModeActive)
              .forEach((event) => event.set('splitModeActive', false));
            ref.current.instance.resumeRefresh(true);
          },
          scheduleMouseMove: (event) => {
            const { resourceRecord, tickStartDate, tickEndDate, source, row } =
              event;

            const resourceId = resourceRecord.get('id');

            if (isDraggingRef.current) {
              return;
            }
            const resourceType = resourceRecord.get('type');

            try {
              // Returning if drag placeholder need not be displayed
              if (!newEventAllowedOn.includes(resourceType)) {
                DomHelper.applyStyle(placeholder as HTMLElement, {
                  display: 'none',
                });

                return;
              }
              const eventsOnParticularTimeFrame = (
                ref.current as unknown as any
              )?.instance.eventStore.getEvents({
                resourceRecord,
                startDate: tickStartDate,
                endDate: tickEndDate,
              });

              if (resourceRecord.get('autoAllocation') === true) {
                DomHelper.applyStyle(placeholder as HTMLElement, {
                  display: 'none',
                });

                return;
              }

              if (eventsOnParticularTimeFrame.length > 0) {
                if (
                  !(
                    eventsOnParticularTimeFrame.length === 1 &&
                    eventsOnParticularTimeFrame[0].get('type') ===
                      SchedulerEventType.UserTimeOff
                  )
                ) {
                  DomHelper.applyStyle(placeholder as HTMLElement, {
                    display: 'none',
                  });

                  return;
                }
              }

              const zoomLevel = (ref.current as unknown as any)?.instance
                .zoomLevel;
              const { incrementBy } = zoomPresets[zoomLevel];
              const startDateMoment = moment(tickStartDate);
              const endDateMoment = moment(tickEndDate);
              const startDate =
                incrementBy === 'week' || isHoursPerWeek
                  ? startDateMoment.startOf('week').toDate()
                  : tickStartDate;
              const endDate =
                incrementBy === 'week' || isHoursPerWeek
                  ? startDateMoment.endOf('week').toDate()
                  : tickEndDate;

              const left =
                source.getCoordinateFromDate(startDate, {
                  local: true,
                }) + 1;
              const width = source.getTimeSpanDistance(startDate, endDate) - 2;

              const top = row.top || DomHelper.getPageY(row.elements.locked);

              placeholderProperties.current = {
                dragStartDate: startDate,
                left,
                width,
                top: top + 12,
              };

              DomHelper.applyStyle(placeholder as HTMLElement, {
                display: 'block',
              });

              DomSync.sync({
                domConfig: {
                  children: [{ html: '<span>+</span>' }],
                  dataset: {
                    placeholder:
                      incrementBy === 'week' || isHoursPerWeek
                        ? `${humanise(
                            shiftDateIfOnNonWorkingDay(
                              startDateMoment.startOf('week'),
                              workingDaysConfig,
                            ),
                          )} - ${humanise(
                            shiftDateIfOnNonWorkingDay(
                              endDateMoment.endOf('week'),
                              workingDaysConfig,
                              false,
                            ),
                          )}`
                        : humanise(startDateMoment),
                    resourceId,
                  },
                  style: {
                    left: `${left}px`,
                    width: `${width}px`,
                    top: `${top + 12}px`,
                    display: 'block',
                  },
                },
                targetElement: placeholder,
              });
            } catch (e) {
              logger.info('error in scheduler mouse move', { e });
            }
          },
        },
        useInitialAnimation: false,
        enableEventAnimations: false,
        columns,
        features: {
          taskEdit: false,
          dependencies: false,
          tree: true,
          stickyEvents: true,
          eventDrag: {
            constrainDragToResource: true,
            tooltipTemplate: ({ startDate, endDate }) =>
              `${humanise(moment(startDate))} - ${humanise(moment(endDate))}`,
          },
          eventResize: {
            tooltipTemplate: ({ startDate, endDate }) =>
              `${humanise(
                shiftDateIfOnNonWorkingDay(
                  moment(startDate),
                  workingDaysConfig,
                ),
              )} - ${humanise(
                shiftDateIfOnNonWorkingDay(
                  moment(endDate).add(1, 'minute').subtract(1, 'day'),
                  workingDaysConfig,
                  false,
                ),
              )}`,
          },
          scheduleMenu: false,
          eventMenu: false,
          cellEdit: false,
          scheduleTooltip: false,
          eventTooltip: false,
          useInitialAnimation: false,
          enableEventAnimations: false,
          eventCopyPaste: false,
          eventFilter: false,
          cellMenu: false,
          columnPicker: false,
          group: false,
          headerMenu: false,
          timeAxisHeaderMenu: false,
          sort: false,
          eventDragCreate: false,
          resourceNonWorkingTime: true,
          resourceTimeRanges: true,
          nonWorkingTime: {
            maxTimeAxisUnit: 'month',
            hideRangesOnZooming: false,
            highlightWeekends: false,
          },
          timeRanges: {
            enableResizing: false,
            showCurrentTimeLine: false,
            showHeaderElements: true,
            tooltipTemplate: ({ timeRange }) => timeRange.name,
          },
        },

        eventRenderer(arg) {
          const { eventRecord, renderData } = arg;
          const startDate = eventRecord.get('startDate');
          const endDate = eventRecord.get('endDate');
          const viewPresetId = ref.current?.instance.viewPreset?.id;
          const minumumDaysForText =
            getMinimumNumberOfDaysForTextToBeVisible(viewPresetId);
          const minumumDaysForMoreActions =
            getMinimumDaysForMoreActionsVisibility(viewPresetId);
          const minumumDaysForDragAffordance =
            getMinimumDaysForDragAffordanceVisibility(viewPresetId);

          if (eventRecord.type === SchedulerEventType.Allocation) {
            if (resourceManagementConfig?.softAllocations.enabled) {
              if (eventRecord.get('medium') === AllocationMedium.Soft) {
                renderData.cls.soft = true;
                renderData.cls.hard = false;
                eventRecord.cls.soft = true;
                eventRecord.cls.hard = false;
                renderData.wrapperCls.add(
                  `${AllocationMedium.Soft}`.toLowerCase(),
                );
                renderData.wrapperCls.remove(
                  `${AllocationMedium.Hard}`.toLowerCase(),
                );
              } else if (eventRecord.get('medium') === AllocationMedium.Hard) {
                renderData.cls.soft = false;
                renderData.cls.hard = true;
                eventRecord.cls.soft = false;
                eventRecord.cls.hard = true;
                renderData.wrapperCls.add(
                  `${AllocationMedium.Hard}`.toLowerCase(),
                );
                renderData.wrapperCls.remove(
                  `${AllocationMedium.Soft}`.toLowerCase(),
                );
              }
            }

            const { resource } = eventRecord;

            if (resource.get('autoAllocation') === true) {
              eventRecord.readOnly = true;
            } else {
              eventRecord.readOnly = false;
            }

            if (moment(startDate).isBefore(moment(endDate))) {
              const numberOfDays = numberOfWorkingDaysBetweenDates(
                moment(startDate),
                moment(endDate),
                workingDaysConfig,
              );

              if (numberOfDays < minumumDaysForText) {
                renderData.wrapperCls.add('hide-content');
              } else {
                renderData.wrapperCls.remove('hide-content');
              }

              if (
                numberOfDays < minumumDaysForMoreActions ||
                eventRecord.readOnly
              ) {
                renderData.wrapperCls.add('hide-more-actions');
              } else {
                renderData.wrapperCls.remove('hide-more-actions');
              }

              if (numberOfDays < minumumDaysForDragAffordance) {
                eventRecord.resizable = false;
              } else {
                eventRecord.resizable = true;
              }
            }
          }

          renderData.wrapperCls.add(eventRecord.cls);
          if (
            eventRecord.type === SchedulerEventType.ProjectTimeline ||
            eventRecord.type === SchedulerEventType.ProjectPhase
          ) {
            if (eventRecord.get('showInDetail')) {
              renderData.wrapperCls.remove('collapsed');
            } else {
              renderData.wrapperCls.add('collapsed');
            }
          }
          if (eventRecord.type === SchedulerEventType.ProjectPhase) {
            renderData.wrapperCls[`top-${eventRecord.top}`] = true;
            renderData.wrapperCls[
              `height-${eventRecord.activePhase ? 24 : eventRecord.height}`
            ] = true;
          }

          if (eventRecord.type === SchedulerEventType.UserAllocationSummary) {
            const { availabilityFilter } = ref.current.instance;

            if (availabilityFilter) {
              const isEventOverlapping = isOverlapping(
                {
                  startDate: moment(eventRecord.startDate),
                  endDate: moment(eventRecord.endDate),
                },
                {
                  startDate: availabilityFilter.startDate,
                  endDate: availabilityFilter.endDate,
                },
              );

              /**
               * I am checking only event overlap here, as the
               * availability check is already applied in filters
               * Is that fine ?
               */
              if (isEventOverlapping) {
                renderData.wrapperCls.add('highlight');
              } else {
                renderData.wrapperCls.remove('highlight');
              }
            } else {
              renderData.wrapperCls.remove('highlight');
            }
          }

          if (eventRecord.get('active')) {
            renderData.cls.isActive = true;
            renderData.wrapperCls.add('isActive');
          } else {
            renderData.wrapperCls.remove('isActive');
            renderData.cls.isActive = false;
          }
          renderData.cls.splitMode = eventRecord.get('splitModeActive');

          const overAllocations = eventRecord.get('overAllocationsForUI');
          const intensities = eventRecord.get('intensities');
          const splitPositions: any[] = [];

          if (eventRecord.get('splitModeActive')) {
            const zoomLevel = (ref.current as unknown as any)?.instance
              .zoomLevel;
            const { incrementBy: incrementByFromZoom } = zoomPresets[zoomLevel];
            const incrementBy = isHoursPerWeek ? 'week' : incrementByFromZoom;
            const startDateMoment =
              incrementBy === 'week'
                ? moment(startDate).startOf('week')
                : moment(startDate);
            const endDateMoment = moment(endDate);
            let increment = 1;

            while (
              startDateMoment
                .add(increment, incrementBy as any)
                .isSameOrBefore(endDateMoment, 'day')
            ) {
              splitPositions.push({
                left: dateToPx(
                  startDateMoment.add(increment, incrementBy as any).toDate(),
                  renderData.left,
                ),
                date: startDateMoment.add(increment, incrementBy as any),
              });
              increment += 1;
            }
          }

          return {
            name:
              eventRecord.get('type') === 'allocation'
                ? isHoursPerWeek
                  ? `${ignoreMinutesIfZero(
                      eventRecord.get('minutes') *
                        (eventRecord.id < 0
                          ? 1
                          : Object.values(workingDaysConfig).filter(Boolean)
                              .length),
                    )}/w`
                  : `${ignoreMinutesIfZero(eventRecord.get('minutes'))}/d`
                : eventRecord.name,
            type: eventRecord.get('type'),
            id: eventRecord.id,
            startDate: eventRecord.startDate,
            endDate: eventRecord.endDate,
            activePhase: eventRecord.activePhase,
            phaseName: eventRecord.phaseName,
            editMode: eventRecord.get('editMode'),
            allocatedMinutes: eventRecord.allocatedMinutes,
            availableMinutes: eventRecord.availableMinutes,
            usersAllocatedMinutes: eventRecord.usersAllocatedMinutes,
            teamsAllocatedMinutes: eventRecord.teamsAllocatedMinutes,
            percentage: eventRecord.minutesPercentage,
            capacity: eventRecord.capacity,
            roleId: eventRecord.roleId,
            timeOff: eventRecord.get('timeOff'),
            splitPositions,
            overAllocationsData: (overAllocations ?? []).map(
              (overallocation) => {
                const startDatePx = ref.current?.instance.getCoordinateFromDate(
                  overallocation.startDate,
                );
                const endDatePx = ref.current?.instance.getCoordinateFromDate(
                  overallocation.endDate,
                );

                return {
                  left: startDatePx - renderData.left,
                  width: endDatePx - startDatePx,
                  overAllocatedMinutes: overallocation.overAllocatedMinutes,
                };
              },
            ),
            intensitiesData: (intensities || []).map((intensity) => ({
              left: dateToPx(
                DateHelper.add(
                  startDate,
                  DateHelper.diff(
                    eventRecord.get('startDate'),
                    DateHelper.parse(intensity.startDate, 'YYYY-MM-DD'),
                  ),
                ),
                renderData.left,
              ),
              width: dateToPx(
                DateHelper.add(
                  startDate,
                  DateHelper.diff(
                    DateHelper.parse(intensity.startDate, 'YYYY-MM-DD'),
                    DateHelper.parse(intensity.endDate, 'YYYY-MM-DD'),
                  ),
                ),
                renderData.left,
              ),
              cls: `level-${intensity.numberOfTasks}`,
              numberOfTasks: intensity.numberOfTasks,
            })),
          };
        },
        eventBodyTemplate: (record) => {
          switch (record.type) {
            case SchedulerEventType.UserAllocationSummary:
              return userSummaryTemplate({
                allocatedMinutes: record.allocatedMinutes,
                capacity: record.capacity,
                id: record.id,
                forViewPreset: ref.current?.instance.viewPreset?.id,
                timeOff: record.timeOff,
              });
            case SchedulerEventType.TeamAllocationSummary:
              return teamSummaryTemplate({
                allocatedMinutes: record.allocatedMinutes,
                id: record.id,
                forViewPreset: ref.current?.instance.viewPreset?.id,
              });
            case SchedulerEventType.CompanyAllocationSummary:
              return companySummaryTemplate({
                allocatedMinutes: record.allocatedMinutes,
                id: record.id,
                forViewPreset: ref.current?.instance.viewPreset?.id,
              });
            case SchedulerEventType.ProjectPhase:
              return projectPhaseTemplate({
                id: record.id,
                phaseName: record.phaseName,
                activePhase: record.activePhase,
              });
            case SchedulerEventType.ProjectTimeline:
              return projectTimelineTemplate(
                {
                  id: record.id,
                  startDate: record.startDate,
                  endDate: record.endDate,
                },
                translate,
              );
            case SchedulerEventType.UserTimeOff:
              return timeOffTemplate({
                id: record.id,
              });
            case SchedulerEventType.DemandRole:
              return demandRoleTemplate({
                id: record.id,
                roleId: record.roleId,
                usersAllocatedMinutes: record.usersAllocatedMinutes,
                teamsAllocatedMinutes: record.teamsAllocatedMinutes,
                capacity: record.capacity,
              });
            case SchedulerEventType.DemandCapacity:
              return demandCapacityTemplate({
                id: record.id,
                capacity: record.capacity,
              });
            case SchedulerEventType.TotalAllocation:
              return totalAllocationTemplate({
                id: record.id,
                roleId: record.roleId,
                usersAllocatedMinutes: record.usersAllocatedMinutes,
                teamsAllocatedMinutes: record.teamsAllocatedMinutes,
              });
            case SchedulerEventType.UserTotalAllocation:
            case SchedulerEventType.UserTotalSoftAllocation:
            case SchedulerEventType.UserTotalHardAllocation:
            case SchedulerEventType.TeamTotalAllocation:
            case SchedulerEventType.TeamTotalSoftAllocation:
            case SchedulerEventType.TeamTotalHardAllocation:
              return demandEventTemplate({
                id: record.id,
                value: record.allocatedMinutes,
                percentage: record.percentage,
              });
            case SchedulerEventType.Allocation:
              return `<div class="content" data-event-id=${record.id}>${
                record.name
              }</div>${(record.overAllocationsData ?? [])
                .map(
                  (value) =>
                    `<div class="over-allocation-bar" data-tooltip="${ignoreMinutesIfZero(
                      value.overAllocatedMinutes,
                    )} over" style="left: ${value.left}px;width: ${
                      value.width
                    }px"></div>`,
                )
                .join('')}${(record.intensitiesData ?? [])
                .map(
                  (value) =>
                    `<div class="intensity-bar ${value.cls}" data-task-number=${value.numberOfTasks} style="left: ${value.left}px;width: ${value.width}px"></div>`,
                )
                .join('')}${record.splitPositions
                .map(
                  ({ date, left }) =>
                    `<div class="splitter" style="left: ${left}px" data-split-at=${date.format(
                      'YYYY-MM-DD',
                    )}><div class="white-bar"></div></div>`,
                )
                .join('')}${
                record.type === 'allocation'
                  ? '<div class="more-actions-container"></div>'
                  : ''
              }`;

            default:
              return '';
          }
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        // columns is removed from the dependency to avoid re-render, we need to fix re-computing of columns
        // columns,
        dateToPx,
        deleteEvent,
        newEventAllowedOn,
        placeholder,
        ref,
        translate,
        workingDaysConfig,
        zoomPresets,
      ],
    );

    const config = merge(schedulerConfig, externalSchedulerConfig);

    useEffect(() => {
      DomHelper.append(
        ref.current.instance.timeAxisSubGridElement,
        placeholder,
      );
    }, [placeholder, ref]);

    return (
      <SchedulerPro className={className}>
        <BryntumSchedulerPro ref={ref} {...config} />
      </SchedulerPro>
    );
  },
);
